import api from './api';

export interface Event {
  id: string;
  name: string;
  description: string;
  location: string;
  sportType: string;
  eventType: string;
  lat: number;
  lng: number;
  startDate: string;
  endDate: string;
  participantsCount?: number;
  maxParticipants?: number;
  price?: number;
  isFree?: boolean;
  difficulty?: string;
  organizer?: string;
  organizerId?: number;
  placeId?: number;
  accessLevel?: string;
  userParticipant?: boolean;
}

interface ApiEvent {
  id: number;
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  placeId?: number;
  placeName?: string;
  timeStart?: number;
  timeFinish?: number;
  organizerId?: number;
  organizerName?: string;
  accessLevel?: string;
  userParticipant?: boolean;
}

const mapApiEvent = (e: ApiEvent): Event => ({
  id: String(e.id),
  name: e.name,
  description: e.description || '',
  location: e.placeName || '',
  sportType: '',
  eventType: e.accessLevel || '',
  lat: e.latitude,
  lng: e.longitude,
  startDate: e.timeStart ? new Date(e.timeStart).toISOString() : '',
  endDate: e.timeFinish ? new Date(e.timeFinish).toISOString() : '',
  organizer: e.organizerName,
  organizerId: e.organizerId,
  placeId: e.placeId,
  accessLevel: e.accessLevel,
  userParticipant: e.userParticipant,
});

export interface EventSearchParams {
  eventType?: string;
  sportType?: string;
  startDate?: Date;
  endDate?: Date;
}

// Backend expects LocalDateTime (no timezone, no milliseconds) in the user's local time.
const toLocalDateTime = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

type CalendarResponse = Record<string, Record<string, Record<string, Record<string, ApiEvent[]>>>>;

const flattenCalendar = (data: CalendarResponse): ApiEvent[] => {
  const out: ApiEvent[] = [];
  for (const year of Object.values(data || {})) {
    for (const month of Object.values(year || {})) {
      for (const week of Object.values(month || {})) {
        for (const day of Object.values(week || {})) {
          out.push(...day);
        }
      }
    }
  }
  return out;
};

/**
 * Fetch events for the calendar view.
 * Uses /api/events/calendar which filters by date range and respects access rights
 * (public events + private events where the authenticated user is a participant).
 */
export const fetchEvents = async (params: EventSearchParams = {}): Promise<Event[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.eventType) {
      queryParams.append('type', params.eventType);
    }
    if (params.sportType) {
      queryParams.append('badgeId', params.sportType);
    }
    if (params.startDate) {
      queryParams.append('startTime', toLocalDateTime(params.startDate));
    }
    if (params.endDate) {
      queryParams.append('endTime', toLocalDateTime(params.endDate));
    }

    const url = `/api/events/calendar${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get<CalendarResponse>(url);

    return flattenCalendar(response.data || {}).map(mapApiEvent);
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

/**
 * Fetch a single event by ID via /api/events/{id}
 */
export const fetchEventById = async (id: string): Promise<Event | null> => {
  try {
    const response = await api.get<ApiEvent>(`/api/events/${id}`);
    return response.data ? mapApiEvent(response.data) : null;
  } catch (error) {
    console.error('Error fetching event by ID:', error);
    return null;
  }
};

export type ParticipationLevel = 'PARTICIPANT' | 'INVITED_INTERESTED';

export interface EventUser {
  id: number;
  alias: string;
  email?: string;
}

interface ApiPersonName {
  fn?: string;
  ln?: string;
}

interface ApiUser {
  id: number;
  alias: string;
  email?: string;
  person?: ApiPersonName;
}

export const fetchEventUsers = async (
  eventId: string,
  level: ParticipationLevel
): Promise<EventUser[]> => {
  try {
    const response = await api.get<ApiUser[]>(
      `/api/events_users/${eventId}?level=${level}`
    );
    return (response.data || []).map((u) => ({
      id: u.id,
      alias: u.alias,
      email: u.email,
    }));
  } catch (error) {
    console.error(`Error fetching event users (${level}):`, error);
    return [];
  }
};

export interface UserSearchResult {
  id: number;
  alias: string;
  email?: string;
  fullName: string;
}

export const searchPotentialUsers = async (
  text: string,
  eventId: string
): Promise<UserSearchResult[]> => {
  if (!text || text.length < 3) return [];
  try {
    const response = await api.get<ApiUser[]>(
      `/api/users?name=${encodeURIComponent(text)}&eventId=${eventId}`
    );
    return (response.data || []).map((u) => {
      const fn = u.person?.fn ? ` ${u.person.fn}` : '';
      const ln = u.person?.ln ? ` ${u.person.ln}` : '';
      return {
        id: u.id,
        alias: u.alias,
        email: u.email,
        fullName: `${u.alias || ''}${fn}${ln}`.trim(),
      };
    });
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

export const inviteUserToEvent = async (
  eventId: string,
  email: string
): Promise<string> => {
  const response = await api.post<{ message?: string }>('/api/events_users', {
    email,
    eventId: Number(eventId),
  });
  return response.data?.message || 'invited';
};

export interface EventBadge {
  id?: number;
  eventId: number;
  badgeId: number;
  name?: string;
}

export const fetchEventBadges = async (eventId: string): Promise<EventBadge[]> => {
  try {
    const response = await api.get<EventBadge[]>(`/api/events-badges?eventId=${eventId}`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching event badges:', error);
    return [];
  }
};

export const addEventBadge = async (
  eventId: string,
  badgeId: number
): Promise<EventBadge | null> => {
  try {
    const body = new URLSearchParams();
    body.append('eventId', String(eventId));
    body.append('badgeId', String(badgeId));
    const response = await api.post<EventBadge>('/api/events-badges', body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data || null;
  } catch (error) {
    console.error('Error adding event badge:', error);
    return null;
  }
};

export const removeEventBadge = async (
  eventId: string,
  badgeId: number
): Promise<boolean> => {
  try {
    await api.delete(`/api/events-badges/event-id/${eventId}/badge-id/${badgeId}`);
    return true;
  } catch (error) {
    console.error('Error removing event badge:', error);
    return false;
  }
};

export interface EventUpdatePayload {
  id: number;
  name: string;
  accessLevel: string;
  description?: string;
  timeStart: number;
  timeFinish?: number | null;
  placeId?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  placeName?: string | null;
}

export const updateEvent = async (payload: EventUpdatePayload): Promise<Event | null> => {
  try {
    const response = await api.put<ApiEvent>(`/api/events/${payload.id}`, payload);
    return response.data ? mapApiEvent(response.data) : null;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (id: string, force = false): Promise<boolean> => {
  try {
    await api.delete(`/api/events/${id}${force ? '?force=true' : ''}`);
    return true;
  } catch (error) {
    console.error('Error deleting event:', error);
    return false;
  }
};

export interface PlaceInfo {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  comment?: string;
  assignment?: string;
}

export const fetchPlaceById = async (id: number | string): Promise<PlaceInfo | null> => {
  try {
    const response = await api.get<PlaceInfo>(`/api/places/${id}`);
    return response.data || null;
  } catch (error) {
    console.error('Error fetching place by id:', error);
    return null;
  }
};
